import { z } from 'zod';
import {
  planFeatures,
  subscriptionPlans,
  type BillingPeriod,
  type PlanFeature,
  type SubscriptionPlan,
  type SubscriptionStatus,
} from '../enums.js';

/**
 * Catalogue des offres : prix, quotas et fonctionnalités.
 *
 * **Une seule source, lue par le serveur et par le front.** Le serveur y puise ce qu'il autorise,
 * le front ce qu'il affiche et ce qu'il verrouille. La V1 avait déjà ce fichier, et c'est l'une des
 * rares choses qu'elle faisait bien ; ce qu'elle faisait mal, c'est de laisser le client décider
 * seul — un quota qui n'est vérifié que dans le navigateur n'est pas un quota.
 *
 * Grille reprise de la V1, décision du 19 septembre 2026, sauf l'offre gratuite, retirée le
 * 26 septembre 2026 au profit d'un essai de 30 jours. L'ordre des offres est porté par
 * `subscriptionPlans` et les leviers sont nommés une fois pour toutes.
 *
 * **Ce fichier ne sait pas encaisser.** Il dit ce qu'une offre coûte et ce qu'elle ouvre ; il ne
 * dit rien du prestataire de paiement, qui n'est pas choisi. Voir `docs/BACKLOG.md`.
 */

/** Quota sans limite. `-1` plutôt que `null` : une comparaison numérique reste une comparaison. */
export const UNLIMITED = -1;

export interface PlanPricing {
  /** Prix mensuel en centimes, sans engagement. */
  readonly monthlyCents: number;
  /** Prix mensuel en centimes quand l'année est payée d'avance. */
  readonly annualMonthlyCents: number;
  /** Total annuel en centimes. Donné, pas recalculé : c'est le montant réellement facturé. */
  readonly annualCents: number;
}

export interface PlanQuotas {
  /** Dossiers patients. `UNLIMITED` au-delà de l'offre Essentiel. */
  readonly patients: number;
  /** Praticiens du cabinet, propriétaire compris. Seule l'offre Cabinet en permet plusieurs. */
  readonly practitioners: number;
}

export interface PlanDefinition {
  readonly plan: SubscriptionPlan;
  readonly pricing: PlanPricing;
  readonly quotas: PlanQuotas;
  /** Les six leviers, tous présents, vrais ou faux. Un `Record` total plutôt qu'une liste. */
  readonly features: Readonly<Record<PlanFeature, boolean>>;
}

export const NO_FEATURES: Readonly<Record<PlanFeature, boolean>> = {
  clinicalTests: false,
  waltonDiagram: false,
  aiMedicalLetters: false,
  prioritySupport: false,
  configurableDashboard: false,
  weroAutoReconciliation: false,
};

const PROFESSIONAL_FEATURES: Readonly<Record<PlanFeature, boolean>> = {
  clinicalTests: true,
  waltonDiagram: true,
  aiMedicalLetters: true,
  prioritySupport: true,
  configurableDashboard: true,
  weroAutoReconciliation: true,
};

/**
 * Le catalogue. Total sur `SubscriptionPlan` : une cinquième offre ne compilera pas tant qu'elle
 * n'aura pas son prix, ses quotas et ses six drapeaux.
 */
export const PLAN_CATALOG: Readonly<Record<SubscriptionPlan, PlanDefinition>> = {
  essentiel: {
    plan: 'essentiel',
    pricing: { monthlyCents: 1_900, annualMonthlyCents: 1_500, annualCents: 18_000 },
    quotas: { patients: 100, practitioners: 1 },
    features: NO_FEATURES,
  },
  professionnel: {
    plan: 'professionnel',
    pricing: { monthlyCents: 3_900, annualMonthlyCents: 3_000, annualCents: 36_000 },
    quotas: { patients: UNLIMITED, practitioners: 1 },
    features: PROFESSIONAL_FEATURES,
  },
  cabinet: {
    plan: 'cabinet',
    pricing: { monthlyCents: 6_900, annualMonthlyCents: 5_500, annualCents: 66_000 },
    quotas: { patients: UNLIMITED, practitioners: 5 },
    features: PROFESSIONAL_FEATURES,
  },
};

/** Rang d'une offre dans l'échelle. Sert à comparer, jamais à afficher. */
export function planRank(plan: SubscriptionPlan): number {
  return subscriptionPlans.indexOf(plan);
}

/**
 * L'offre à partir de laquelle une fonctionnalité s'ouvre, ou `null` si aucune ne l'ouvre.
 * C'est ce que l'infobulle de verrouillage affiche : « disponible à partir de Professionnel ».
 */
export function firstPlanWith(feature: PlanFeature): SubscriptionPlan | null {
  return subscriptionPlans.find((plan) => PLAN_CATALOG[plan].features[feature]) ?? null;
}

export function planHasFeature(plan: SubscriptionPlan, feature: PlanFeature): boolean {
  return PLAN_CATALOG[plan].features[feature];
}

/** Vrai si le quota est sans limite, ou si l'usage y tient encore. */
export function withinQuota(limit: number, used: number): boolean {
  return limit === UNLIMITED || used < limit;
}

export function priceCentsFor(plan: SubscriptionPlan, period: BillingPeriod): number {
  const { pricing } = PLAN_CATALOG[plan];
  return period === 'annual' ? pricing.annualCents : pricing.monthlyCents;
}

/* -------------------------------------------------------------------------- */
/* Contrat réseau                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Ce qu'un cabinet a le droit de faire, tel que le serveur le calcule.
 *
 * Renvoyé au front pour qu'il **affiche** les verrous, jamais pour qu'il les applique : chaque
 * route les vérifie de son côté. La V1 cachait un bouton et laissait la route ouverte.
 */
export interface EntitlementsDto {
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  period: BillingPeriod;
  /** Les six leviers résolus pour ce cabinet. Tous faux quand `readOnly` est vrai. */
  features: Record<PlanFeature, boolean>;
  quotas: {
    patients: { limit: number; used: number };
    practitioners: { limit: number; used: number };
  };
  /**
   * Fin de la période en cours : fin de l'essai ou fin de la période payée. Nulle tant qu'aucune
   * échéance n'est connue.
   */
  currentPeriodEnd: string | null;
  /**
   * Vrai quand le cabinet est **bloqué pour l'appelant** : essai terminé ou abonnement expiré, hors
   * équipe de la plateforme (décision du 11 octobre 2026 : blocage total, lecture comprise). Le
   * nom est historique. Le front s'en sert pour afficher l'écran de blocage ; **le serveur refuse
   * de son côté** (`402 subscription_expired`).
   */
  readOnly: boolean;
  /** Fin de l'essai non converti, pour un cabinet `expired` ; nulle sinon, ou après un abonnement payé. */
  trialEndedAt: string | null;
  /** Date d'effacement prévue d'un cabinet `expired` ; nulle sinon, ou si aucune horloge ne court. */
  scheduledDeletionAt: string | null;
}

/** Une offre du catalogue telle que la page Tarifs la montre. */
export interface PlanOfferDto {
  plan: SubscriptionPlan;
  monthlyCents: number;
  annualMonthlyCents: number;
  annualCents: number;
  patientLimit: number;
  practitionerLimit: number;
  features: Record<PlanFeature, boolean>;
}

export function toPlanOfferDto(plan: SubscriptionPlan): PlanOfferDto {
  const definition = PLAN_CATALOG[plan];
  return {
    plan,
    monthlyCents: definition.pricing.monthlyCents,
    annualMonthlyCents: definition.pricing.annualMonthlyCents,
    annualCents: definition.pricing.annualCents,
    patientLimit: definition.quotas.patients,
    practitionerLimit: definition.quotas.practitioners,
    features: { ...definition.features },
  };
}

export const planOffers: readonly PlanOfferDto[] = subscriptionPlans.map(toPlanOfferDto);

export { planFeatures, subscriptionPlans };

/* -------------------------------------------------------------------------- */
/* Administration de la plateforme                                             */
/* -------------------------------------------------------------------------- */

export const organizationIdParamSchema = z.object({ organizationId: z.uuid() }).strict();

/**
 * Changement d'offre d'un cabinet. **Route de plateforme uniquement** : l'offre commande les
 * quotas et les quatre leviers, donc un `PUT` ouvert au propriétaire lui donnerait l'offre Cabinet
 * en une requête. Tant qu'aucun prestataire d'encaissement n'est branché, c'est le support qui
 * pose l'offre après un règlement reçu hors application.
 */
export const organizationPlanUpdateSchema = z.object({ plan: z.enum(subscriptionPlans) }).strict();
export type OrganizationPlanUpdateInput = z.infer<typeof organizationPlanUpdateSchema>;

/** Ce que le changement d'offre rend : l'ancienne et la nouvelle, pour que la trace soit lisible. */
export interface OrganizationPlanDto {
  organizationId: string;
  organizationName: string;
  previousPlan: SubscriptionPlan;
  plan: SubscriptionPlan;
  updatedAt: string;
}
